import React, { useState, useRef, useEffect } from "react";
import { Share2, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Reusable share menu: Copy link, Share on Twitter/X, Share on LinkedIn.
// Pass trackId (a job id) to record share-button clicks for analytics.
export default function ShareMenu({ url, text, testId = "share", size = "sm", trackId }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const track = () => { if (trackId) api.post(`/jobs/${trackId}/share`).catch(() => {}); };

  const copy = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true); toast.success("Link copied"); track();
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Could not copy link"); }
  };

  const twitter = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
  const stop = (e) => e.stopPropagation();
  const pad = size === "sm" ? "p-2" : "px-3 py-2";

  return (
    <div className="relative" ref={ref} onClick={stop}>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        data-testid={`${testId}-btn`}
        aria-label="Share"
        className={`${pad} rounded-md border border-white/12 text-[#94A3B8] hover:text-[#00F0FF] hover:border-[#00F0FF]/40 transition inline-flex items-center gap-1.5`}>
        <Share2 className="w-4 h-4" />
        {size !== "sm" && <span className="text-sm font-semibold">Share</span>}
      </button>
      {open && (
        <div data-testid={`${testId}-menu`}
          className="absolute right-0 z-40 mt-2 w-52 rounded-lg border border-white/12 bg-[#0B0F19] shadow-xl p-1.5">
          <button onClick={copy} data-testid={`${testId}-copy`}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-[#e2e8f0] hover:bg-white/5 transition">
            {copied ? <Check className="w-4 h-4 text-[#22C55E]" /> : <Link2 className="w-4 h-4 text-[#00F0FF]" />}
            {copied ? "Copied!" : "Copy link"}
          </button>
          <a href={twitter} target="_blank" rel="noopener noreferrer" onClick={track} data-testid={`${testId}-twitter`}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-[#e2e8f0] hover:bg-white/5 transition">
            <svg className="w-4 h-4 fill-current text-[#e2e8f0]" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            Share on X (Twitter)
          </a>
          <a href={linkedin} target="_blank" rel="noopener noreferrer" onClick={track} data-testid={`${testId}-linkedin`}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-[#e2e8f0] hover:bg-white/5 transition">
            <svg className="w-4 h-4 fill-current text-[#0A66C2]" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/></svg>
            Share on LinkedIn
          </a>
        </div>
      )}
    </div>
  );
}
